import React, { useRef, useState, useEffect } from 'react';
import { ShieldCheck, Zap, Award, Sparkles, ArrowRight, CheckCircle2, MapPin, Calendar, BookOpen, HelpCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { SpecialistAvatar } from './SpecialistAvatar';
import { useSite } from '../context/SiteContext';

interface HeroProps {
  onOpenCalculator: () => void;
  onOpenLeadModal: (title?: string) => void;
}

export const Hero: React.FC<HeroProps> = ({ onOpenCalculator, onOpenLeadModal }) => {
  let isFemale = false;
  let siteName = 'Bursa Erkek Lazer Epilasyon';
  let badgeText = 'Hekim Denetimli Erkek Medikal Kliniği';

  try {
    const site = useSite();
    if (site) {
      isFemale = site.isFemale;
      siteName = site.siteProfile.siteName;
      badgeText = site.siteProfile.badgeText || (isFemale ? 'Kadın Estetisyen Eşliğinde %100 Mahremiyet' : 'Hekim Denetimli Erkek Medikal Kliniği');
    }
  } catch {}

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 5);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  useEffect(() => {
    let animId: number;
    const deferredCheck = () => {
      animId = requestAnimationFrame(() => {
        checkScroll();
      });
    };

    deferredCheck();
    const el = scrollContainerRef.current;
    if (el) {
      el.addEventListener('scroll', deferredCheck, { passive: true });
      window.addEventListener('resize', deferredCheck, { passive: true });
      return () => {
        cancelAnimationFrame(animId);
        el.removeEventListener('scroll', deferredCheck);
        window.removeEventListener('resize', deferredCheck);
      };
    }
  }, []);

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const offset = direction === 'left' ? -200 : 200;
      scrollContainerRef.current.scrollBy({ left: offset, behavior: 'smooth' });
    }
  };
  const quickNav = [
    { href: '#bolgeler', label: 'Bölgeler', fullLabel: 'Uygulama Bölgeleri', icon: Sparkles },
    { href: '#fiyat-hesapla', label: 'Seans Planı', fullLabel: 'Bölge & Seans Planı', icon: Calendar, highlight: true },
    { href: '#doktor-guvencesi', label: 'Doktor Güvencesi', fullLabel: 'Doktor Güvencesi', icon: ShieldCheck },
    { href: '#teknoloji', label: 'Buz Lazer', fullLabel: 'Buz Teknolojisi', icon: Zap },
    { href: '#blog', label: 'Rehber', fullLabel: 'Rehber & Blog', icon: BookOpen },
    { href: '#sss', label: 'S.S.S', fullLabel: 'Sıkça Sorulanlar', icon: HelpCircle },
    { href: '#iletisim', label: 'Konum', fullLabel: 'Klinik Konumu', icon: MapPin },
  ];
  return (
    <section id="hero-section" className="relative overflow-hidden pt-6 pb-10 lg:pt-10 lg:pb-14 bg-gradient-to-b from-white via-slate-50 to-slate-100/80">
      {/* Background Decorative Medical Ambient Glows */}
      <div className={`absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[350px] blur-[130px] rounded-full pointer-events-none ${
        isFemale ? 'bg-rose-200/35' : 'bg-amber-200/30'
      }`} />
      <div className="absolute top-1/2 -left-20 w-80 h-80 bg-sky-200/40 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 xl:px-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 xl:gap-16 items-center">
          
          {/* Left Column: Core Value Proposition & Headings */}
          <div className="lg:col-span-7 xl:col-span-7 space-y-4 sm:space-y-5 text-center lg:text-left">
            
            {/* Trust Pill / Location & Clinic Badge */}
            <div className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold shadow-2xs ${
              isFemale 
                ? 'bg-rose-50 border border-rose-200 text-rose-900' 
                : 'bg-amber-50 border border-amber-300 text-amber-900'
            }`}>
              <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <MapPin className={`w-3.5 h-3.5 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />
              <span>Bursa Nilüfer • Fatih Sultan Mehmet (FSM) Bulvarı</span>
              <span className="hidden md:inline opacity-30">|</span>
              <span className="hidden md:inline font-semibold opacity-90">{badgeText}</span>
            </div>

            {/* Main H1 Title - SEO & Conversion Optimized */}
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.15]">
              <span className="block text-slate-900 mb-1">
                {isFemale ? 'Bursa Kadın Lazer Epilasyon' : 'Bursa Erkek Lazer Epilasyon'}
              </span>
              <span className={`block ${isFemale ? 'text-rose-600' : 'text-amber-600'}`}>
                Doktor Kliniğinde Buz Lazer
              </span>
            </h1>

            {/* Subtitle / Description */}
            <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto lg:mx-0 font-normal leading-relaxed">
              {isFemale ? (
                <>
                  Kadınların hassas ten ve kıl kökü yapısına özel <strong className="text-slate-900 font-semibold">3 dalga boylu hibrit buz başlık teknolojisi</strong>. 
                  FSM Bulvarı’nda hekim kontrolünde; tüm bacak, genital, koltuk altı ve <strong className="text-slate-900 font-semibold">Bursa kadın lazer epilasyon</strong> seanslarında acısız, steril ve kalıcı medikal estetik çözümler.
                </>
              ) : (
                <>
                  Erkeklerin kalın ve derin kıl kökü yapısına özel <strong className="text-slate-900 font-semibold">hibrit buz başlık teknolojisi</strong>. 
                  FSM Bulvarı’nda hekim kontrolünde; sırt, göğüs, sakal ve <strong className="text-slate-900 font-semibold">Bursa erkek özel bölge lazer epilasyon</strong> seanslarında acısız, steril ve tamamen gizlilik odaklı medikal çözümler.
                </>
              )}
            </p>

            {/* Key Bullet Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 max-w-xl mx-auto lg:mx-0 text-left">
              <div className="flex items-center gap-2 text-xs text-slate-800 bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span><strong className="text-slate-900">Doktor Kliniği:</strong> Sıfır yanık/leke riski</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-800 bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span><strong className="text-slate-900">Safir Buz Başlık:</strong> -5°C acısız seans</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-800 bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>
                  <strong className="text-slate-900">{isFemale ? 'Pürüzsüz Cilt:' : 'Sakal & Batık:'}</strong> {isFemale ? 'Batık & lekeye son' : 'Kalıcı berber çizgisi'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-800 bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>
                  <strong className="text-slate-900">{isFemale ? 'Kadın Estetisyen:' : 'Kişiye Özel Paket:'}</strong> {isFemale ? '%100 mahremiyet' : 'Şeffaf fiyatlandırma'}
                </span>
              </div>
            </div>

            {/* Main Action CTAs */}
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <button
                type="button"
                id="hero-livechat-btn"
                onClick={() => {
                  if (typeof window !== 'undefined' && window.openLiveChat) {
                    window.openLiveChat();
                  } else if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open-live-chat'));
                  }
                }}
                className={`w-full sm:w-auto px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2.5 shadow-sm shadow-slate-900/20 border transition-all hover:scale-[1.01] group cursor-pointer ${
                  isFemale ? 'border-rose-900/60 hover:border-rose-400/50' : 'border-slate-700/90 hover:border-amber-500/40'
                }`}
              >
                <SpecialistAvatar size="xs" showOnlineStatus={true} bordered={false} site={isFemale ? 'kadin' : 'erkek'} />
                <span className={isFemale ? 'group-hover:text-rose-300 transition-colors' : 'group-hover:text-amber-300 transition-colors'}>
                  {isFemale ? 'Kadın Uzman İle Canlı Görüşün' : 'Uzman İle Canlı Görüşün'}
                </span>
                <ArrowRight className={`w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform ${isFemale ? 'text-rose-400' : 'text-amber-400'}`} />
              </button>

              <a
                id="hero-calc-cta"
                href="#fiyat-hesapla"
                onClick={onOpenCalculator}
                className="w-full sm:w-auto px-4 py-3 rounded-xl bg-white hover:bg-slate-100/90 text-slate-800 hover:text-slate-950 font-bold text-xs sm:text-sm border border-slate-200/90 hover:border-slate-300 flex items-center justify-center gap-2 transition-all shadow-2xs"
              >
                <Calendar className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />
                <span>Bölge Seç & Randevu Planla</span>
              </a>
            </div>

            {/* Specialist Reassurance Note with Live Presence */}
            <div className={`inline-flex items-center justify-center lg:justify-start gap-2 pt-1 px-3 py-1.5 rounded-xl border max-w-fit mx-auto lg:mx-0 ${
              isFemale 
                ? 'bg-rose-500/10 border-rose-300/60' 
                : 'bg-amber-500/10 border-amber-300/60'
            }`}>
              <SpecialistAvatar size="xs" showOnlineStatus={true} bordered={true} site={isFemale ? 'kadin' : 'erkek'} />
              <p className="text-[11px] text-slate-700">
                <strong className="text-slate-900 font-bold">{isFemale ? 'Uzman Kadın Estetisyen:' : 'Uzmanımız:'}</strong>{' '}
                <span className="text-emerald-700 font-bold">● Şu An Çevrim İçi</span> —{' '}
                {isFemale ? 'Kadın seans paketleri ve cilt tipinize dair sorularınızı anında yanıtlar.' : 'Kıl yapınıza ve seans planınıza dair sorularınızı anında yanıtlar.'}
              </p>
            </div>
          </div>

          {/* Right Column: High-Trust Clinical Showcase Card */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none bg-white rounded-2xl border border-slate-200/90 shadow-xl shadow-slate-200/50 overflow-hidden">
              
              {/* Card Top Banner - Medical Authority Badge */}
              <div className="bg-slate-900 text-white px-5 py-3.5 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border ${
                    isFemale ? 'bg-rose-500/20 border-rose-400/40' : 'bg-amber-500/20 border-amber-400/40'
                  }`}>
                    <ShieldCheck className={`w-4 h-4 ${isFemale ? 'text-rose-400' : 'text-amber-400'}`} />
                  </div>
                  <div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider block ${isFemale ? 'text-rose-400' : 'text-amber-400'}`}>
                      {isFemale ? 'Medikal Estetik Protokolü' : 'Medikal Protokol'}
                    </span>
                    <p className="font-bold text-xs sm:text-sm text-white leading-tight">
                      {isFemale ? 'FSM Bulvarı Kadın Kliniği' : 'FSM Bulvarı Erkek Kliniği'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-[10px] font-bold text-emerald-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span>Randevu Açık</span>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-5 sm:p-6 space-y-4">
                
                {/* 3 Core Clinical Values Bar */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-2.5">
                    <span className="text-[10px] font-bold text-slate-700 uppercase tracking-tight block">Soğutma</span>
                    <strong className="text-base font-black text-slate-900 block mt-0.5">-5°C</strong>
                    <span className={`text-[10px] font-semibold ${isFemale ? 'text-rose-800' : 'text-amber-800'}`}>
                      {isFemale ? 'Safir Buz Konforu' : 'Acısız Buz Başlık'}
                    </span>
                  </div>
                  <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-2.5">
                    <span className="text-[10px] font-bold text-slate-700 uppercase tracking-tight block">Kalıcılık</span>
                    <strong className="text-base font-black text-slate-900 block mt-0.5">%90+</strong>
                    <span className="text-[10px] font-semibold text-emerald-700">
                      {isFemale ? 'İpeksi Pürüzsüzlük' : 'Kalıcı Azalma'}
                    </span>
                  </div>
                  <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-2.5">
                    <span className="text-[10px] font-bold text-slate-700 uppercase tracking-tight block">Protokol</span>
                    <strong className="text-base font-black text-slate-900 block mt-0.5">
                      {isFemale ? 'Uzman' : 'Hekim'}
                    </strong>
                    <span className="text-[10px] font-semibold text-slate-700">
                      {isFemale ? 'Kadın Estetisyen' : 'Kontrollü Seans'}
                    </span>
                  </div>
                </div>

                {/* Specific Benefits List */}
                <div className="space-y-2 pt-1">
                  <div className="text-xs font-bold text-slate-900 flex items-center justify-between">
                    <span>{isFemale ? 'Kadınlara Özel Klinik Avantajları' : 'Erkeklere Özel Klinik Avantajları'}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                      isFemale 
                        ? 'text-rose-700 bg-rose-50 border-rose-200' 
                        : 'text-amber-700 bg-amber-50 border-amber-200'
                    }`}>
                      Bursa Nilüfer
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    {isFemale ? (
                      <>
                        <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/70 text-xs text-slate-800">
                          <div className="w-5 h-5 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-slate-900">Tüm Bacak, Genital & Koltuk Altı Paketi:</span>
                            <span className="text-slate-600 text-[11px] block">Batıklara, çilek bacağa ve lekelere son</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/70 text-xs text-slate-800">
                          <div className="w-5 h-5 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-slate-900">Yüz, Bıyık & Çene İnce Tüy Seansı:</span>
                            <span className="text-slate-600 text-[11px] block">Tüyleri tetiklemeden kalıcı ve ipeksi pürüzsüzlük</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/70 text-xs text-slate-800">
                          <div className="w-5 h-5 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-slate-900">Uzman Kadın Estetisyenler & %100 Mahremiyet:</span>
                            <span className="text-slate-600 text-[11px] block">Konforlu, steril ve tek kullanımlık hijyen kiti</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/70 text-xs text-slate-800">
                          <div className="w-5 h-5 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-slate-900">Safir Buz Başlık ile Dört Mevsim Güven:</span>
                            <span className="text-slate-600 text-[11px] block">Bronz tende dahi acısız ve sıfır yanık/leke riski</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/70 text-xs text-slate-800">
                          <div className="w-5 h-5 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-slate-900">Sakal Üstü, Ense & Boyun Batık Tedavisi:</span>
                            <span className="text-slate-600 text-[11px] block">Tıraş tahrişine ve kıl dönmesine son</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/70 text-xs text-slate-800">
                          <div className="w-5 h-5 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-slate-900">Sırt, Omuz & Göğüs Seyreltme / Bitirme:</span>
                            <span className="text-slate-600 text-[11px] block">İster doğal seyreltme, ister pürüzsüz görünüm</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/70 text-xs text-slate-800">
                          <div className="w-5 h-5 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-slate-900">Bursa Erkek Özel Bölge Lazer Epilasyon:</span>
                            <span className="text-slate-600 text-[11px] block">%100 mahremiyet, erkek uzman ve hijyen protokolü</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/70 text-xs text-slate-800">
                          <div className="w-5 h-5 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-slate-900">Tam Gizlilik & Steril VIP Uygulama Odası:</span>
                            <span className="text-slate-600 text-[11px] block">Randevulu, beklemesiz ve konforlu seanslar</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="pt-2 space-y-2">
                  <button
                    id="hero-free-consultation-btn"
                    onClick={() => onOpenLeadModal(isFemale ? 'Kadın Seans Planı ve Ücretsiz Deneme' : 'Ücretsiz Kıl Analizi ve Seans Planı')}
                    className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-2 shadow-sm cursor-pointer"
                  >
                    <Sparkles className={`w-4 h-4 ${isFemale ? 'text-rose-400' : 'text-amber-400'}`} />
                    <span>{isFemale ? 'Ücretsiz Cilt Analizi & Seans Planı Al' : 'Ücretsiz Kıl Analizi ve Seans Planı Al'}</span>
                  </button>

                  <div className="flex items-center justify-between text-[11px] text-slate-700 font-medium px-1 pt-0.5">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-slate-600" />
                      FSM Bulvarı, Nilüfer
                    </span>
                    <span className="text-emerald-700 font-semibold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Aynı Gün Ön Görüşme
                    </span>
                  </div>
                </div>

              </div>

            </div>
          </div>

        </div>

        {/* Section Navigation Bar - Ultra-Responsive Smooth Carousel & Desktop Grid */}
        <div className="mt-8 sm:mt-10 pt-5 border-t border-slate-200/80">
          <div className="relative max-w-6xl mx-auto">
            {/* Left Scroll Button (Mobile/Tablet) */}
            {canScrollLeft && (
              <button
                onClick={() => handleScroll('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white/95 shadow-md border border-slate-200 text-slate-700 flex items-center justify-center hover:bg-slate-50 transition-all cursor-pointer"
                aria-label="Sola kaydır"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}

            {/* Scrollable Container with Subtle Edge Fade Gradient */}
            <div 
              ref={scrollContainerRef}
              className="flex items-center justify-start sm:justify-center gap-1.5 sm:gap-2 lg:gap-2.5 overflow-x-auto no-scrollbar py-1 px-2 scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            >
              {quickNav.map((link) => {
                const IconComponent = link.icon;
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    className={`inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 lg:px-3.5 py-2 sm:py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap shadow-2xs group cursor-pointer shrink-0 select-none ${
                      link.highlight
                        ? 'bg-slate-900 text-white hover:bg-slate-800 border border-slate-900 hover:shadow-xs'
                        : 'bg-white hover:bg-amber-50/80 text-slate-700 hover:text-amber-800 border border-slate-200/90 hover:border-amber-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-colors ${
                      link.highlight
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-slate-100 group-hover:bg-amber-100 text-slate-600 group-hover:text-amber-700'
                    }`}>
                      <IconComponent className="w-3.5 h-3.5" />
                    </div>
                    <span>
                      <span className="inline lg:hidden">{link.label}</span>
                      <span className="hidden lg:inline">{link.fullLabel}</span>
                    </span>
                  </a>
                );
              })}
            </div>

            {/* Right Scroll Button (Mobile/Tablet) */}
            {canScrollRight && (
              <button
                onClick={() => handleScroll('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white/95 shadow-md border border-slate-200 text-slate-700 flex items-center justify-center hover:bg-slate-50 transition-all cursor-pointer"
                aria-label="Sağa kaydır"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

      </div>
    </section>
  );
};
